import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    ChevronLeft, ChevronRight, CheckCircle2, XCircle,
    Trophy, RefreshCw, ArrowLeft, CheckSquare, Square, Download
} from 'lucide-react';
import NavigationHeader from '../../components/ui/NavigationHeader';
import { getAuthToken } from '../../utils/cookies';
import { generateCertificate } from '../../utils/generateCertificate';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MermaidDiagram from '../../components/ui/MermaidDiagram';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';

const Quiz = () => {
    const { categorySlug } = useParams();

    const [quiz, setQuiz] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [previousAttempt, setPreviousAttempt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // One-at-a-time state
    const [currentIdx, setCurrentIdx] = useState(0);
    // selections: { question_id: Set<option_id> }
    const [selections, setSelections] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [userName, setUserName] = useState('');

    useEffect(() => { fetchQuiz(); fetchUserName(); }, [categorySlug]);

    const fetchUserName = async () => {
        try {
            const token = getAuthToken();
            const res = await fetch(`${API_BASE}/auth/profile`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                credentials: 'include',
            });
            if (res.ok) {
                const d = await res.json();
                setUserName(d.full_name || d.username || d.email || 'Learner');
            }
        } catch { /* silently ignore */ }
    };

    const fetchQuiz = async () => {
        setLoading(true);
        setResult(null);
        setSelections({});
        setCurrentIdx(0);
        try {
            const token = getAuthToken();
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            const res = await fetch(`${API_BASE}/api/learn/quiz/${categorySlug}`, {
                headers,
                credentials: 'include',
            });
            if (!res.ok) throw new Error('No quiz found for this category');
            const data = await res.json();
            setQuiz(data.quiz);
            setQuestions(data.questions || []);
            setPreviousAttempt(data.attempt || null);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleOption = (qid, oid) => {
        setSelections(prev => {
            const current = new Set(prev[qid] || []);
            if (current.has(oid)) current.delete(oid);
            else current.add(oid);
            return { ...prev, [qid]: [...current] };
        });
    };

    const isSelected = (qid, oid) => (selections[qid] || []).includes(oid);

    const currentQ = questions[currentIdx];
    const totalQ = questions.length;
    const currentAnswered = currentQ ? (selections[currentQ.id] || []).length > 0 : false;
    const allAnswered = questions.every(q => (selections[q.id] || []).length > 0);

    const handleSubmit = async () => {
        const answers = {};
        for (const q of questions) {
            answers[q.id] = selections[q.id] || [];
        }
        setSubmitting(true);
        try {
            const token = getAuthToken();
            const res = await fetch(`${API_BASE}/api/learn/quiz/${quiz.id}/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                },
                credentials: 'include',
                body: JSON.stringify({ answers }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Submission failed');
            setResult(data);
            setCurrentIdx(0);
        } catch (e) {
            alert(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    // ── Loading / Error ──────────────────────────────────────────────

    if (loading) return (
        <div className="min-h-screen bg-white font-sans">
            <NavigationHeader />
            <main className="pt-28 flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-emerald-400 border-t-transparent rounded-full animate-spin" />
            </main>
        </div>
    );

    if (error) return (
        <div className="min-h-screen bg-white font-sans">
            <NavigationHeader />
            <main className="pt-28 px-4 text-center">
                <div className="text-5xl mb-4">📭</div>
                <p className="text-xl font-normal text-slate-500 mb-2">No Quiz Available</p>
                <p className="text-slate-500 mb-6">{error}</p>
                <Link to="/learn" className="text-emerald-600 hover:underline font-medium">← Back to Learn</Link>
            </main>
        </div>
    );

    // ── Results View ─────────────────────────────────────────────────

    if (result) {
        const results = result.results || [];
        return (
            <div className="min-h-screen bg-white font-sans">
                <NavigationHeader />
                <main className="pt-24 pb-16 px-4">
                    <div className="max-w-xl mx-auto space-y-4">
                        {/* Score hero */}
                        <div className={`rounded-3xl p-8 text-center shadow-lg ${result.passed
                            ? 'bg-gradient-to-br from-emerald-500 via-teal-500 to-emerald-600 text-white'
                            : 'bg-white border border-slate-200'}`}>
                            {result.passed ? (
                                <>
                                    <Trophy size={52} className="mx-auto mb-3 text-yellow-300 drop-shadow" />
                                    <div className="text-6xl font-black mb-1">{result.score}%</div>
                                    <div className="text-emerald-100 text-xl font-bold mb-1">You Passed! 🎉</div>
                                    <div className="text-emerald-200 text-sm">{result.correct}/{result.total} correct · Certificate Earned</div>
                                </>
                            ) : (
                                <>
                                    <div className="text-6xl font-black text-slate-900 mb-1">{result.score}%</div>
                                    <div className="text-slate-600 text-lg font-bold mb-1">Almost there!</div>
                                    <div className="text-slate-400 text-sm">{result.correct}/{result.total} correct · Need {result.passing_score}% to pass</div>
                                </>
                            )}
                        </div>

                        {/* Certificate banner */}
                        {result.certificate && (
                            <div className="flex items-center gap-4 bg-amber-50 border border-amber-200 rounded-2xl p-4">
                                <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                                    <Trophy size={22} className="text-amber-500" />
                                </div>
                                <div className="flex-1">
                                    <div className="font-bold text-slate-900 text-sm">Certificate Issued!</div>
                                    <div className="text-xs text-slate-500">Added to your dashboard trophies.</div>
                                </div>
                                <button
                                    onClick={() => generateCertificate({
                                        userName,
                                        quizTitle: quiz?.title,
                                        categoryName: quiz?.category_name,
                                        score: result.score,
                                        issuedAt: new Date().toISOString(),
                                    })}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-xl transition-colors shrink-0"
                                >
                                    <Download size={13} /> Download
                                </button>
                            </div>
                        )}

                        {/* Per-question breakdown */}
                        <div className="space-y-2">
                            {results.map((r, idx) => (
                                <div key={r.question_id}
                                    className={`bg-white rounded-xl border p-4 ${r.is_correct ? 'border-emerald-200' : 'border-red-200'}`}>
                                    <div className="flex items-start gap-2">
                                        {r.is_correct
                                            ? <CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" />
                                            : <XCircle size={18} className="text-red-400 shrink-0 mt-0.5" />}
                                        <div>
                                            <div className="text-[11px] font-bold text-slate-400 mb-0.5">Q{idx + 1}</div>
                                            <div className="text-sm font-medium text-slate-800">{r.question}</div>
                                            {r.explanation && (
                                                <div className="mt-2 text-xs text-slate-600 bg-blue-50/50 rounded-lg px-4 py-3 border border-blue-100/50 prose prose-sm prose-slate max-w-none prose-p:my-1 prose-pre:my-2 prose-pre:bg-slate-900 prose-pre:text-slate-100">
                                                    <ReactMarkdown
                                                        remarkPlugins={[remarkGfm]}
                                                        components={{
                                                            code({ node, inline, className, children, ...props }) {
                                                                const match = /language-(\w+)/.exec(className || '');
                                                                if (!inline && match && match[1] === 'mermaid') {
                                                                    return <MermaidDiagram chart={String(children).replace(/\n$/, '')} />;
                                                                }
                                                                return !inline ? (
                                                                    <code className={className} {...props}>
                                                                        {children}
                                                                    </code>
                                                                ) : (
                                                                    <code className="bg-slate-100 text-slate-800 px-1 py-0.5 rounded text-[11px] font-mono" {...props}>
                                                                        {children}
                                                                    </code>
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        {r.explanation}
                                                    </ReactMarkdown>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 pt-2">
                            <button onClick={fetchQuiz}
                                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-white border border-slate-200 text-slate-700 font-bold hover:bg-slate-50 transition-colors">
                                <RefreshCw size={15} /> Retake
                            </button>
                            <Link to="/learn"
                                className="flex-1 flex items-center justify-center py-3 rounded-xl bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition-colors">
                                Back to Learn
                            </Link>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    // ── Quiz Taking View ─────────────────────────────────────────────

    const progress = totalQ > 0 ? ((currentIdx + 1) / totalQ) * 100 : 0;

    return (
        <div className="min-h-screen bg-white font-sans">
            <NavigationHeader />
            
            {/* Premium Quiz Header (Emerald Effect) - Standard with platform visual identity */}
            <div className="relative pt-12 pb-6 overflow-hidden bg-gradient-to-b from-emerald-50 to-white border-b border-emerald-100/30">
                <div className="max-w-2xl mx-auto px-6 relative z-10 transition-all duration-500">
                    {/* Minimal Path - Breadcrumb style title navigation */}
                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-400 mb-4tracking-widest overflow-x-auto whitespace-nowrap hide-scrollbar">
                        <Link to="/learn" className="hover:text-emerald-600 transition-colors">Learn</Link>
                        <ChevronRight size={12} className="text-slate-300 stroke-[3]" />
                        <span className="text-emerald-600">{quiz?.title}</span>
                    </div>
-
                    {/* Attempt Status & Score Potential */}
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-5">
                        <div>
                            <div className="text-[10px] font-black text-slate-400tracking-widest mb-1 opacity-60">LATEST STATUS</div>
                            {previousAttempt ? (
                                <div className="flex items-center gap-2">
                                    <div className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${previousAttempt.passed ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                                        {previousAttempt.passed ? 'PASSED' : 'NOT PASSED'}
                                    </div>
                                    <span className="text-xs font-bold text-slate-700">{previousAttempt.score}% Score</span>
                                </div>
                            ) : (
                                <div className="text-[11px] font-normal text-slate-500 italic">No previous attempts</div>
                            )}
                        </div>
                        <div className="text-right">
                            <div className="text-[10px] font-black text-slate-400tracking-widest mb-1 opacity-60">QUIZ COMPLETION</div>
                            <div className="text-base font-black text-emerald-600 flex items-center justify-end gap-1.5">
                                <span>{Math.round(progress)}%</span>
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            </div>
                        </div>
                    </div>

                    {/* Premium Progress Bar */}
                    <div className="mb-6">
                        <div className="h-1.5 bg-slate-200/50 rounded-full overflow-hidden shadow-inner border border-white/40">
                            <div
                                className="h-full bg-emerald-500 rounded-full transition-all duration-1000 cubic-bezier(0.4, 0, 0.2, 1) shadow-[0_0_12px_rgba(16,185,129,0.3)]"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>

                    {/* Question navigation dots - Premium UI */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {questions.map((q, i) => {
                            const answered = (selections[q.id] || []).length > 0;
                            const isCurrent = i === currentIdx;
                            return (
                                <button
                                    key={q.id}
                                    onClick={() => setCurrentIdx(i)}
                                    className={`w-8 h-8 rounded-lg text-[11px] font-black transition-all duration-300 flex items-center justify-center border ${isCurrent
                                        ? 'bg-emerald-500 text-white border-emerald-400 shadow-md shadow-emerald-500/20 scale-105 z-10'
                                        : answered
                                            ? 'bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100'
                                            : 'bg-white text-slate-400 border-slate-100 hover:border-slate-200 hover:text-slate-600'}`}
                                >
                                    {i + 1}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <main className="pt-4 pb-20 px-4 font-sans max-w-2xl mx-auto">
                <h1 className="text-2xl font-black text-slate-900 tracking-tight mb-6 leading-tight">
                    {quiz?.title}
                </h1>
                
                {/* Question card */}
                {currentQ && (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 animate-in fade-in slide-in-from-bottom-4 duration-300">
                        <div className="text-[10px] font-bold text-slate-400tracking-widest mb-1.5">
                            Question {currentIdx + 1}
                        </div>
                        <h2 className="text-[16px] font-bold text-slate-900 leading-snug mb-4">
                            {currentQ.question}
                        </h2>

                        <div className="space-y-1.5">
                            {(currentQ.options || []).map((o, oi) => {
                                const sel = isSelected(currentQ.id, o.id);
                                return (
                                    <button
                                        key={o.id}
                                        type="button"
                                        onClick={() => toggleOption(currentQ.id, o.id)}
                                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl border text-left transition-all duration-150 group ${sel
                                            ? 'bg-emerald-50 border-emerald-400 text-emerald-900 shadow-sm'
                                            : 'bg-slate-50 border-slate-200 hover:border-slate-300 hover:bg-white text-slate-700'}`}
                                    >
                                        <div className={`shrink-0 w-4 h-4 flex items-center justify-center transition-colors ${sel ? 'text-emerald-500' : 'text-slate-300 group-hover:text-slate-400'}`}>
                                            {sel ? <CheckSquare size={18} /> : <Square size={18} />}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className={`text-[11px] font-bold w-4 shrink-0 ${sel ? 'text-emerald-400' : 'text-slate-400'}`}>
                                                {String.fromCharCode(65 + oi)}.
                                            </span>
                                            <span className="font-bold text-sm">{o.option_text}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <p className="text-[10px] font-medium text-slate-400 mt-3">Multiple answers may be correct — select all that apply.</p>
                    </div>
                )}

                {/* Navigation */}
                <div className="flex items-center gap-3 mt-4">
                    <button
                        onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
                        disabled={currentIdx === 0}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white border border-slate-200 text-slate-600 font-bold text-xs hover:bg-slate-50 disabled:opacity-40 transition-colors"
                    >
                        <ChevronLeft size={14} /> Prev
                    </button>

                    <div className="flex-1" />

                    {currentIdx < totalQ - 1 ? (
                        <button
                            onClick={() => setCurrentIdx(i => i + 1)}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-bold text-xs transition-all ${currentAnswered
                                ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm shadow-emerald-200'
                                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                        >
                            Next <ChevronRight size={14} />
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={!allAnswered || submitting}
                            className={`flex items-center gap-2 px-5 py-2 rounded-lg font-bold text-xs transition-all ${allAnswered
                                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-200 hover:shadow-lg hover:from-emerald-600 hover:to-teal-600'
                                : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                        >
                            {submitting ? 'Submitting...' : 'Submit Quiz →'}
                        </button>
                    )}
                </div>

                {!allAnswered && currentIdx === totalQ - 1 && (
                    <p className="text-center text-xs text-slate-400 mt-3">
                        Answer all questions before submitting. Use the dots above to navigate.
                    </p>
                )}
            </main>
        </div>
    );
};

export default Quiz;
